import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { contactInfo } from "@/data/mock";
import {
  diagnosticSections,
  qualitativeQuestions,
  scaleOptions,
  ragConfig,
  startCopy,
  computeSnapshot,
} from "@/data/diagnostic";
import { ArrowRight, ArrowLeft, Check, RefreshCw, MessageSquare, AlertCircle, Download, Lightbulb, Scale } from "lucide-react";
import { toast } from "sonner";
import { generateSnapshotPdf } from "@/lib/snapshotPdf";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "teqie-diagnostic-v1";
// stages: intro(0), 7 sections(1-7), qualitative(8), contact(9), results(10)
const COMPLETION_STAGES = 9;

const track = (event, props = {}) => {
  if (window.posthog) window.posthog.capture(event, props);
};

function loadSaved() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

export default function DiagnosticPage() {
  const saved = loadSaved();
  const [step, setStep] = useState(saved.step || 0);
  const [scores, setScores] = useState(saved.scores || {});
  const [qual, setQual] = useState(saved.qual || { frustration: "", ninety_days: "", opportunity: "" });
  const [contact, setContact] = useState(saved.contact || { first_name: "", business_name: "", email: "", phone: "", website: "", sector: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [interp, setInterp] = useState(null);

  useEffect(() => {
    document.title = "Growth & Efficiency Diagnostic | Teqie";
    track("diagnostic_viewed");
  }, []);

  useEffect(() => {
    if (step > 0 && step < 10) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, scores, qual, contact }));
    }
  }, [step, scores, qual, contact]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (window.__lenis) window.__lenis.scrollTo(0, { duration: 0.6 });
  }, [step]);

  const answeredCount = Object.keys(scores).length;
  const progress = Math.min(100, Math.round((step / COMPLETION_STAGES) * 100));

  const setScore = (qid, value) => setScores((s) => ({ ...s, [qid]: value }));

  const sectionComplete = (idx) =>
    diagnosticSections[idx].questions.every((q) => scores[q.id] !== undefined);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email);
  const contactValid = contact.first_name.trim() && contact.business_name.trim() && emailValid && consent;

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(false);
    try {
      const res = await axios.post(`${API}/api/diagnostic`, {
        scores,
        frustration: qual.frustration,
        ninety_days: qual.ninety_days,
        opportunity: qual.opportunity,
        ...contact,
        consent,
      });
      const snap = res.data.snapshot || computeSnapshot(scores);
      setSnapshot(snap);
      setInterp(res.data.interpretation || null);
      setStep(10);
      localStorage.removeItem(STORAGE_KEY);
      track("diagnostic_completed");
    } catch (err) {
      setSubmitError(true);
      toast.error("Your diagnostic couldn't be saved. Your answers are safe — please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [scores, qual, contact, consent]);

  return (
    <div className="min-h-screen bg-[#FAF7F2] pt-28 pb-24" data-testid="diagnostic-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {step > 0 && step < 10 && (
          <div className="mb-10" data-testid="diagnostic-progress">
            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-3">
              <span>
                {step <= 7 ? `Section ${step} of 7 — ${diagnosticSections[step - 1].label}` : step === 8 ? "In your own words" : "About you"}
              </span>
              <span>{answeredCount}/21 answered</span>
            </div>
            <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-brand-600 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {step === 0 && <IntroStep onStart={() => { setStep(1); track("diagnostic_started"); }} hasSaved={answeredCount > 0} />}

        {step >= 1 && step <= 7 && (
          <SectionStep
            section={diagnosticSections[step - 1]}
            scores={scores}
            setScore={setScore}
            onBack={() => setStep(step - 1)}
            onNext={() => { setStep(step + 1); track("diagnostic_section_completed", { section: diagnosticSections[step - 1].key }); }}
            canNext={sectionComplete(step - 1)}
            isFirst={step === 1}
          />
        )}

        {step === 8 && (
          <QualitativeStep qual={qual} setQual={setQual} onBack={() => setStep(7)} onNext={() => setStep(9)} />
        )}

        {step === 9 && (
          <ContactStep
            contact={contact}
            setContact={setContact}
            consent={consent}
            setConsent={setConsent}
            emailValid={emailValid}
            valid={contactValid}
            submitting={submitting}
            submitError={submitError}
            onBack={() => setStep(8)}
            onSubmit={handleSubmit}
          />
        )}

        {step === 10 && snapshot && (
          <ResultsStep snapshot={snapshot} interp={interp} qual={qual} firstName={contact.first_name} businessName={contact.business_name} />
        )}
      </div>
    </div>
  );
}

const IntroStep = ({ onStart, hasSaved }) => (
  <div data-testid="diagnostic-intro">
    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-700">The Teqie Growth & Efficiency Diagnostic</p>
    <h1 className="mt-5 font-display font-semibold text-stone-900 tracking-tight leading-[1.08] text-4xl sm:text-5xl">
      Understand what's really happening in your business.
    </h1>
    <p className="mt-6 text-stone-600 leading-relaxed text-base md:text-lg">
      21 quick statements across seven areas of your business, plus three questions in your own words.
      It takes around 5–7 minutes, and you'll get an immediate Growth & Efficiency Snapshot showing
      your strongest areas, the areas most worth exploring, and where I'd suggest starting.
    </p>
    <ul className="mt-8 space-y-3">
      {["No right or wrong answers — answer for how things actually work today", "Your snapshot appears immediately after you finish — and you can download it as a PDF", "I personally review every diagnostic before a Growth Conversation"].map((t, i) => (
        <li key={i} className="flex items-start gap-3 text-stone-700 text-sm md:text-base">
          <Check className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
          {t}
        </li>
      ))}
    </ul>
    <Button
      onClick={onStart}
      data-testid="diagnostic-start-btn"
      className="mt-10 bg-stone-900 hover:bg-brand-700 text-[#FAF7F2] rounded-full h-13 px-8 py-6 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
    >
      {hasSaved ? "Continue the Diagnostic" : "Start the Diagnostic"}
      <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
    <p className="mt-6 text-xs text-stone-500">
      Your answers are only used to prepare for a potential conversation with you. No spam, ever.
    </p>
  </div>
);

const SHORT_LABELS = { 1: "Strongly\ndisagree", 2: "Disagree", 3: "Somewhere\nin between", 4: "Agree", 5: "Strongly\nagree" };

const SectionStep = ({ section, scores, setScore, onBack, onNext, canNext, isFirst }) => (
  <div data-testid={`diagnostic-section-${section.key}`}>
    <h2 className="font-display font-semibold text-stone-900 tracking-tight text-2xl sm:text-3xl">{section.label}</h2>
    <p className="mt-2 text-sm text-stone-500">How much do you agree with each statement?</p>

    <div className="mt-8 space-y-8">
      {section.questions.map((q) => (
        <fieldset key={q.id} className="rounded-2xl bg-white border border-stone-200 p-6 sm:p-7" data-testid={`question-${q.id}`}>
          <legend className="sr-only">{q.text}</legend>
          <p className="text-base md:text-lg font-medium text-stone-900 leading-snug">{q.text}</p>
          <div className="mt-5 grid grid-cols-5 gap-2 sm:gap-3" role="radiogroup" aria-label={q.text}>
            {scaleOptions.map((opt) => {
              const selected = scores[q.id] === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={opt.label}
                  onClick={() => setScore(q.id, opt.value)}
                  data-testid={`answer-${q.id}-${opt.value}`}
                  className={`flex flex-col items-center justify-center gap-1 rounded-xl border-2 py-3 sm:py-3.5 px-1 transition-all duration-200 min-h-[56px] ${
                    selected
                      ? "border-brand-600 bg-brand-600 text-white shadow-md"
                      : "border-stone-200 bg-[#FAF7F2] text-stone-700 hover:border-brand-400"
                  }`}
                >
                  <span className="text-base font-semibold">{opt.value}</span>
                  <span className={`hidden sm:block text-[10px] leading-tight text-center whitespace-pre-line ${selected ? "text-white/90" : "text-stone-500"}`}>
                    {SHORT_LABELS[opt.value]}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-[11px] text-stone-500 sm:hidden">
            <span>Strongly disagree</span>
            <span>Strongly agree</span>
          </div>
          {scores[q.id] !== undefined && (
            <p className="mt-2 text-sm font-medium text-brand-700" aria-live="polite">
              {scaleOptions.find((o) => o.value === scores[q.id]).label}
            </p>
          )}
        </fieldset>
      ))}
    </div>

    <div className="mt-10 flex items-center justify-between">
      <Button variant="ghost" onClick={onBack} data-testid="diagnostic-back-btn" className="text-stone-600 rounded-full">
        <ArrowLeft className="w-4 h-4 mr-2" />
        {isFirst ? "Intro" : "Back"}
      </Button>
      <Button
        onClick={onNext}
        disabled={!canNext}
        data-testid="diagnostic-next-btn"
        className="bg-stone-900 hover:bg-brand-700 text-[#FAF7F2] rounded-full h-12 px-8 text-sm font-medium disabled:opacity-40"
      >
        Next
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  </div>
);

const QualitativeStep = ({ qual, setQual, onBack, onNext }) => (
  <div data-testid="diagnostic-qualitative">
    <h2 className="font-display font-semibold text-stone-900 tracking-tight text-2xl sm:text-3xl">In your own words</h2>
    <p className="mt-2 text-sm text-stone-500">Optional, but the most useful part for preparing a conversation. "I'm not sure" is a perfectly good answer.</p>
    <div className="mt-8 space-y-6">
      {qualitativeQuestions.map((q) => (
        <div key={q.id} className="rounded-2xl bg-white border border-stone-200 p-6 sm:p-7">
          <label htmlFor={q.id} className="block text-base font-medium text-stone-900 leading-snug mb-3">
            {q.label}
          </label>
          <Textarea
            id={q.id}
            value={qual[q.id]}
            onChange={(e) => setQual((s) => ({ ...s, [q.id]: e.target.value }))}
            data-testid={`qualitative-${q.id}`}
            rows={3}
            maxLength={2000}
            className="bg-[#FAF7F2] border-stone-200 rounded-xl resize-none focus-visible:ring-brand-500"
            placeholder="Type your answer, or 'I'm not sure'…"
          />
        </div>
      ))}
    </div>
    <div className="mt-10 flex items-center justify-between">
      <Button variant="ghost" onClick={onBack} data-testid="diagnostic-back-btn" className="text-stone-600 rounded-full">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>
      <Button onClick={onNext} data-testid="diagnostic-next-btn" className="bg-stone-900 hover:bg-brand-700 text-[#FAF7F2] rounded-full h-12 px-8 text-sm font-medium">
        Next
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  </div>
);

const ContactStep = ({ contact, setContact, consent, setConsent, emailValid, valid, submitting, submitError, onBack, onSubmit }) => {
  const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
  return (
    <div data-testid="diagnostic-contact">
      <h2 className="font-display font-semibold text-stone-900 tracking-tight text-2xl sm:text-3xl">Almost there — about you</h2>
      <p className="mt-2 text-sm text-stone-500">So I can review your snapshot and prepare properly if you book a conversation.</p>
      <div className="mt-8 rounded-2xl bg-white border border-stone-200 p-6 sm:p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="First name *" id="first_name">
            <Input id="first_name" value={contact.first_name} onChange={set("first_name")} data-testid="contact-first-name" className="bg-[#FAF7F2] border-stone-200 rounded-xl h-11" placeholder="Alex" />
          </Field>
          <Field label="Business name *" id="business_name">
            <Input id="business_name" value={contact.business_name} onChange={set("business_name")} data-testid="contact-business-name" className="bg-[#FAF7F2] border-stone-200 rounded-xl h-11" placeholder="Acme Ltd" />
          </Field>
        </div>
        <Field label="Email *" id="email" error={contact.email && !emailValid ? "Please enter a valid email address" : ""}>
          <Input id="email" type="email" value={contact.email} onChange={set("email")} data-testid="contact-email" className="bg-[#FAF7F2] border-stone-200 rounded-xl h-11" placeholder="alex@acme.co.uk" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Phone (optional)" id="phone">
            <Input id="phone" value={contact.phone} onChange={set("phone")} data-testid="contact-phone" className="bg-[#FAF7F2] border-stone-200 rounded-xl h-11" placeholder="+44…" />
          </Field>
          <Field label="Website (optional)" id="website">
            <Input id="website" value={contact.website} onChange={set("website")} data-testid="contact-website" className="bg-[#FAF7F2] border-stone-200 rounded-xl h-11" placeholder="https://…" />
          </Field>
        </div>
        <Field label="Sector (optional)" id="sector">
          <Input id="sector" value={contact.sector} onChange={set("sector")} data-testid="contact-sector" className="bg-[#FAF7F2] border-stone-200 rounded-xl h-11" placeholder="e.g. Property, E-commerce, Professional services" />
        </Field>
        <div className="flex items-start gap-3 pt-2">
          <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} data-testid="contact-consent" className="mt-0.5" />
          <label htmlFor="consent" className="text-sm text-stone-600 leading-relaxed cursor-pointer">
            I'm happy for Teqie to store my answers and contact me about my results. *
          </label>
        </div>
      </div>

      {submitError && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 flex items-start gap-3" data-testid="diagnostic-submit-error" role="alert">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-semibold">Your diagnostic couldn't be saved — your answers are still here.</p>
            <p className="mt-1">
              Please try again, or{" "}
              <a href={`mailto:${contactInfo.email}`} className="underline font-medium">contact Teqie directly</a>.
            </p>
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} data-testid="diagnostic-back-btn" className="text-stone-600 rounded-full">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!valid || submitting}
          data-testid="diagnostic-submit-btn"
          className="bg-brand-700 hover:bg-brand-800 text-white rounded-full h-12 px-8 text-sm font-medium disabled:opacity-40"
        >
          {submitting ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving…
            </>
          ) : submitError ? (
            "Try Saving Again"
          ) : (
            "See My Snapshot"
          )}
          {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, id, error, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-stone-800 mb-1.5">{label}</label>
    {children}
    {error && <p className="mt-1.5 text-xs text-red-600" role="alert">{error}</p>}
  </div>
);

const ResultsStep = ({ snapshot, interp, qual, firstName, businessName }) => {
  const dims = snapshot.dimensions;
  const byKey = Object.fromEntries(dims.map((d) => [d.key, d]));
  const strongest = byKey[snapshot.strongest];
  const focus = byKey[snapshot.focus];
  const primary = interp?.primary_hypothesis;
  const contradiction = interp?.contradictions?.[0];
  const exploreQs = interp?.exploration_questions || [];
  const ninety = (qual.ninety_days || "").trim();

  return (
    <div data-testid="diagnostic-results">
      {/* 1. Heading */}
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-700">Your Growth & Efficiency Snapshot</p>
      <h1 className="mt-4 font-display font-semibold text-stone-900 tracking-tight leading-[1.1] text-3xl sm:text-4xl lg:text-5xl">
        {firstName ? `Thanks, ${firstName}. ` : ""}Here's what your answers suggest.
      </h1>
      <p className="mt-4 text-stone-600 leading-relaxed">
        This isn't a score out of 100 — it's a pattern. The value is in seeing which areas are supporting the business and which may be quietly constraining it.
      </p>

      {/* 2. Seven dimensions */}
      <div className="mt-10 rounded-3xl bg-white border border-stone-200 p-6 sm:p-8 space-y-5" data-testid="results-dimensions">
        {dims.map((d) => {
          const rag = ragConfig[d.rag];
          return (
            <div key={d.key} data-testid={`result-dimension-${d.key}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-stone-900">{d.label}</span>
                <span className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-stone-700">{d.average.toFixed(1)} / 5</span>
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${rag.chip}`}>{rag.label}</span>
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-stone-100 overflow-hidden">
                <div className={`h-full rounded-full ${rag.bar} transition-all duration-1000`} style={{ width: `${(d.average / 5) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* 3. What stands out */}
      {interp?.what_stands_out && (
        <div className="mt-8 rounded-3xl bg-white border border-stone-200 p-6 sm:p-8" data-testid="results-stands-out">
          <h2 className="font-display font-semibold text-stone-900 text-xl sm:text-2xl">What stands out</h2>
          <p className="mt-4 text-stone-700 leading-relaxed">{interp.what_stands_out}</p>
        </div>
      )}

      {/* 4/5. Strongest + focus */}
      <div className="mt-8 grid sm:grid-cols-2 gap-5">
        <HighlightCard eyebrow="Strongest area" title={strongest.label} value={strongest.average} tone="green" testId="result-strongest" />
        <HighlightCard eyebrow="Most worth exploring" title={focus.label} value={focus.average} tone="focus" testId="result-focus" />
      </div>

      {/* 6. Primary pattern */}
      {primary && (
        <div className="mt-8 rounded-3xl border-2 border-[#A86532]/25 bg-[#A86532]/[0.05] p-6 sm:p-8" data-testid="results-pattern">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A86532]">
            <Lightbulb className="w-4 h-4" />
            A pattern worth exploring
          </div>
          <h2 className="mt-3 font-display font-semibold text-stone-900 text-xl sm:text-2xl leading-snug">{primary.heading}</h2>
          <p className="mt-3 text-stone-700 leading-relaxed">{primary.summary}</p>
          {interp.evidence_plain && (
            <p className="mt-4 text-sm text-stone-600 leading-relaxed border-l-2 border-[#A86532]/40 pl-4">{interp.evidence_plain}</p>
          )}
        </div>
      )}

      {/* 7. Contradiction */}
      {contradiction && (
        <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-6 sm:p-8" data-testid="results-contradiction">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-400">
            <Scale className="w-4 h-4" />
            An interesting tension
          </div>
          <h3 className="mt-3 font-display font-semibold text-stone-900 text-lg leading-snug">{contradiction.heading}</h3>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed">{contradiction.interpretation}</p>
        </div>
      )}

      {/* 8. Their 90-day priority */}
      {ninety && (
        <div className="mt-6 rounded-3xl bg-brand-50 border border-brand-200 p-6 sm:p-8" data-testid="results-ninety-days">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-700">Your 90-day priority, in your words</p>
          <p className="mt-3 font-display text-lg text-stone-800 leading-relaxed">"{ninety}"</p>
        </div>
      )}

      {/* 9. Where I'd start */}
      <div className="mt-8 rounded-3xl bg-[#032e2d] p-8 sm:p-10 grain relative overflow-hidden" data-testid="results-start">
        <h2 className="font-display font-semibold text-white text-2xl sm:text-3xl relative z-10">Where I'd start</h2>
        <p className="mt-4 text-teal-100/85 leading-relaxed text-base md:text-lg relative z-10">
          {interp?.where_id_start || startCopy[snapshot.focus]}
        </p>
        {ninety && (
          <p className="mt-4 text-teal-100/60 text-sm leading-relaxed relative z-10">
            You also told me what you'd most like to improve in the next 90 days — that's exactly the kind of starting point a Growth Conversation is designed around.
          </p>
        )}

        {/* 10. Questions worth exploring */}
        {exploreQs.length > 0 && (
          <div className="mt-8 relative z-10" data-testid="results-explore-questions">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-300 mb-4">Questions worth exploring</p>
            <ul className="space-y-2.5">
              {exploreQs.map((q, i) => (
                <li key={i} className="flex items-start gap-3 text-teal-100/80 text-sm md:text-base leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D9945E] flex-shrink-0" />
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 11. CTA */}
        <div className="mt-9 relative z-10">
          <p className="text-teal-50 font-display text-lg mb-5" data-testid="results-cta-line">
            Your diagnostic tells us where to look. The next step is understanding why.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={() => { track("diagnostic_booking_clicked"); window.open(contactInfo.bookingUrl, "_blank", "noopener,noreferrer"); }}
              data-testid="results-book-cta"
              className="bg-teal-50 hover:bg-white text-[#032e2d] rounded-full h-12 px-7 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5"
            >
              Book My Growth Conversation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => window.open(contactInfo.whatsapp, "_blank", "noopener,noreferrer")}
              data-testid="results-whatsapp-cta"
              variant="outline"
              className="rounded-full h-12 px-7 text-sm font-medium border-teal-100/30 bg-transparent text-teal-50 hover:bg-teal-100/10 hover:text-white"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Message on WhatsApp
            </Button>
            <Button
              onClick={() => { track("diagnostic_pdf_downloaded"); generateSnapshotPdf({ snapshot, interp, qual, firstName, businessName }); }}
              data-testid="results-download-pdf-btn"
              variant="outline"
              className="rounded-full h-12 px-7 text-sm font-medium border-teal-100/30 bg-transparent text-teal-50 hover:bg-teal-100/10 hover:text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download My Snapshot (PDF)
            </Button>
          </div>
          <p className="mt-5 text-sm text-teal-200/60">
            I'll review your responses beforehand, so the conversation starts with context rather than from a blank page.
          </p>
        </div>
      </div>

      {/* Disclaimer + review note */}
      <p className="mt-8 text-sm text-stone-500 leading-relaxed" data-testid="results-disclaimer">
        Your snapshot identifies patterns, not definitive causes. Different problems can create similar
        scores, so the next step is understanding what's behind the pattern and whether changing it
        would create meaningful business value.
      </p>
      <p className="mt-4 text-sm text-stone-500 leading-relaxed" data-testid="results-review-note">
        I review every diagnostic personally. If you decide to book a Growth Conversation, I'll review
        your responses beforehand — so we can spend our time exploring what's behind the results and
        what might be worth doing next.
      </p>
    </div>
  );
};

const HighlightCard = ({ eyebrow, title, value, tone, testId }) => {
  const styles = {
    green: "border-emerald-200 bg-emerald-50/60",
    focus: "border-[#A86532]/30 bg-[#A86532]/[0.06]",
    neutral: "border-stone-200 bg-white",
  };
  return (
    <div className={`rounded-2xl border p-6 ${styles[tone]}`} data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">{eyebrow}</p>
      <p className="mt-2 font-display font-semibold text-stone-900 text-lg leading-snug">{title}</p>
      <p className="mt-1 text-sm text-stone-600">{value.toFixed(1)} / 5</p>
    </div>
  );
};
